import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Guía de puesta en marcha del bot de Telegram (BotFather, token, webhook, vinculación).
 */
export default function GuiaTelegram({ triggerClassName = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5 border-sky-200 text-sky-800 ${triggerClassName}`}
          data-testid="telegram-guia-btn"
        >
          <HelpCircle className="h-4 w-4" />
          Guía
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Configurar Telegram — Ventura HR</DialogTitle>
          <DialogDescription>
            Crea un bot con BotFather, guarda el token aquí y vincula a cada trabajador con un email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-zinc-700">
          <ol className="list-decimal space-y-3 pl-4">
            <li>
              <p className="font-medium text-zinc-900">Crear el bot</p>
              <p>
                En Telegram busca <strong>@BotFather</strong>, envía <code className="rounded bg-zinc-100 px-1">/newbot</code>,
                elige nombre y un username que termine en <code className="rounded bg-zinc-100 px-1">bot</code>.
                Copia el <strong>token</strong> (formato <code className="rounded bg-zinc-100 px-1">123456:ABC…</code>).
              </p>
            </li>
            <li>
              <p className="font-medium text-zinc-900">Guardar en Ajustes</p>
              <p>
                Pega el token y el username del bot (sin @). Activa Telegram y pulsa Guardar.
                Opcionalmente pulsa <strong>Registrar webhook</strong> (requiere URL pública HTTPS,
                p. ej. producción o un túnel en local).
              </p>
            </li>
            <li>
              <p className="font-medium text-zinc-900">Vincular trabajadores</p>
              <p>
                En Empleados, activa Telegram y pulsa <strong>Enviar vínculo</strong>. El trabajador recibe un email
                con el logo y nombre de la empresa, su centro de trabajo y un enlace{" "}
                <code className="rounded bg-zinc-100 px-1">t.me/TuBot?start=CODIGO</code>.
                Al pulsar Iniciar, el bot guarda su <code className="rounded bg-zinc-100 px-1">chat_id</code>.
              </p>
            </li>
            <li>
              <p className="font-medium text-zinc-900">Canales por evento</p>
              <p>
                En «Canales por tipo de notificación» marca Telegram en los eventos que quieras enviar.
                Solo llegan a trabajadores vinculados y con el canal activado.
              </p>
            </li>
          </ol>

          <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2.5 text-xs text-sky-950 space-y-1.5">
            <p className="font-medium">Envío de mensajes</p>
            <p>
              Una vez vinculado, Ventura usa la Bot API:{" "}
              <code className="break-all rounded bg-white/80 px-1">
                POST https://api.telegram.org/bot&lt;TOKEN&gt;/sendMessage
              </code>{" "}
              con <code className="rounded bg-white/80 px-1">chat_id</code> y <code className="rounded bg-white/80 px-1">text</code>.
            </p>
          </div>

          <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2.5 text-xs text-amber-950">
            <p className="font-medium">Local vs producción</p>
            <p className="mt-1">
              En local el webhook solo funciona si Telegram puede alcanzar tu API (HTTPS público).
              En producción configura <code className="rounded bg-white/70 px-1">PUBLIC_API_URL</code> y registra el webhook.
            </p>
          </div>

          <p className="text-xs text-zinc-500">
            Documentación oficial:{" "}
            <a
              href="https://core.telegram.org/bots/tutorial"
              target="_blank"
              rel="noreferrer"
              className="text-sky-700 underline"
            >
              Bot tutorial
            </a>
            {" · "}
            <a
              href="https://core.telegram.org/bots/api#sendmessage"
              target="_blank"
              rel="noreferrer"
              className="text-sky-700 underline"
            >
              sendMessage
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
